import React, { useEffect, useState } from 'react';
import { useAppStore } from '../../../context/AppContext';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { useStowageStore } from 'src/context/StowageContext';
import { getNumberOfIncompleteStowages, getValueOrMax } from 'src/utils/stowage/stowageOperation/rotationCallStatusesUtils';
import { RedIcon } from 'src/components/ui/status-icon/VerificationIcon.styles';

type Props = {
    path: string;
};

type NoonReportDate = {
    date: string;
    isNoonReportCompletedForDay: boolean;
    isNoonReportApprovedForDay: boolean;
};

const useMissingTasksData = () => {
    const {
        missingRobStatuses,
        missingNoonReportStatuses,
        missingCargoTempLogStatuses,
        numMissingReportAol,
        setNumMissingReportAol,
        numMissingApprovedAol,
        setNumMissingApprovedAol,
        numMissingBunkerRegistrations,
        numMissingBunkerRob,
    } = useAppStore();

    const { rotationCallStatuses } = useStowageStore();

    const [numberOfIncompleteStowages, setNumberOfIncompleteStowages] = useState<number | null>(null);
    const [numberOfMissingCargoConsumables, setNumberOfMissingCargoConsumables] = useState<number | null>(null);
    const [numberOfMissingCargoTempLogDates, setNumberOfMissingCargoTempLogDates] = useState<number | null>(null);

    useEffect(() => {
        setNumberOfIncompleteStowages(rotationCallStatuses ? getNumberOfIncompleteStowages(rotationCallStatuses) : null);
    }, [rotationCallStatuses]);

    useEffect(() => {
        setNumberOfMissingCargoConsumables(missingRobStatuses?.length || null);
    }, [missingRobStatuses]);

    useEffect(() => {
        if (!missingNoonReportStatuses) {
            setNumMissingReportAol(missingNoonReportStatuses === null ? null : 14);
            setNumMissingApprovedAol(null);
            return;
        }

        const { toBeApproved, noonReports } = processNoonReportStatuses(missingNoonReportStatuses);

        setNumMissingReportAol(Math.max(14 - toBeApproved - noonReports, 0) || null);
        setNumMissingApprovedAol(toBeApproved || null);
    }, [missingNoonReportStatuses]);

    useEffect(() => {
        const numMissingReportedCargoTempLogs =
            missingCargoTempLogStatuses?.filter(({ isComplete, cargoInTankList }) => !isComplete && cargoInTankList?.length > 0).length || null;
        setNumberOfMissingCargoTempLogDates(numMissingReportedCargoTempLogs);
    }, [missingCargoTempLogStatuses]);

    return {
        numberOfIncompleteStowages,
        numberOfMissingCargoConsumables,
        numberOfMissingCargoTempLogDates,
        numMissingReportAol,
        numMissingApprovedAol,
        numMissingBunkerRegistrations,
        numMissingBunkerRob,
    };
};

const processNoonReportStatuses = (statuses: any[]) => {
    const noonReportStatusListByDay: NoonReportDate[] = [];
    let toBeApproved = 0;
    let noonReports = 0;

    statuses.forEach((status) => {
        const date = prettifyDate(status.noonReportDate);
        let dayStatus = noonReportStatusListByDay.find((n) => n.date === date);

        if (!dayStatus) {
            dayStatus = { date, isNoonReportCompletedForDay: status.isAolComplete, isNoonReportApprovedForDay: status.isAolApproved };
            noonReportStatusListByDay.push(dayStatus);
        } else {
            if (!status.isAolComplete) dayStatus.isNoonReportCompletedForDay = false;
            if (!status.isAolApproved) dayStatus.isNoonReportApprovedForDay = false;
        }
    });

    noonReportStatusListByDay.forEach(({ isNoonReportApprovedForDay, isNoonReportCompletedForDay }) => {
        if (!isNoonReportApprovedForDay && isNoonReportCompletedForDay) toBeApproved++;
        if (isNoonReportApprovedForDay && isNoonReportCompletedForDay) noonReports++;
    });

    return { toBeApproved, noonReports };
};

const renderIcon = (condition: boolean, title: string, value: number | string | null) => {
    return condition && value ? <RedIcon title={title}>{value}</RedIcon> : null;
};

const MissingTasksIcon: React.FC<Props> = ({ path }) => {
    const {
        numberOfIncompleteStowages,
        numberOfMissingCargoConsumables,
        numberOfMissingCargoTempLogDates,
        numMissingReportAol,
        numMissingApprovedAol,
        numMissingBunkerRegistrations,
        numMissingBunkerRob,
    } = useMissingTasksData();

    return (
        <>
            {renderIcon(
                path === 'stowage',
                'Unstowed cargo operations',
                numberOfIncompleteStowages ? getValueOrMax(numberOfIncompleteStowages) : null
            )}
            {renderIcon(path === 'aol', 'Missing/uncompleted tasks last 14 days', numMissingReportAol + numMissingApprovedAol)}
            {renderIcon(path === 'bunkering', 'Missing/uncompleted tasks last 14 days', numMissingBunkerRegistrations + numMissingBunkerRob)}
            {renderIcon(path === 'cargo-temp-log', 'Missing/uncompleted tasks last 14 days', numberOfMissingCargoTempLogDates)}
            {renderIcon(path === 'tank-cleaning', 'Missing/uncompleted tasks last 14 days', numberOfMissingCargoConsumables)}
        </>
    );
};

export default MissingTasksIcon;
